import { ThemeText } from "redux/reducers/theme/themeReducer";
import { COLORS, DARK_THEME_COLORS, LIGHT_THEME_COLORS } from "./colors";
import * as mediaQuery from "components/MediaQuery";
import { ThemeConfig } from "antd/lib";
import { DefaultTheme } from "styled-components";

export const calcRem = (size: number) => `${size / 16}rem`;

export const baseFontFamily =
  '"Roboto", Roboto, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';

const fontSizes = {
  small: calcRem(13),
  base: calcRem(15),
  lg: calcRem(17),
  xl: calcRem(19),
  xxl: calcRem(21),
  xxxl: calcRem(23),
  titleSize: calcRem(50),
};

const paddings = {
  small: calcRem(8),
  base: calcRem(10),
  lg: calcRem(12),
  xl: calcRem(14),
  xxl: calcRem(16),
  xxxl: calcRem(18),
};

const interval = {
  base: calcRem(50),
  lg: calcRem(100),
  xl: calcRem(150),
  xxl: calcRem(200),
};

const verticalInterval = {
  base: `${calcRem(10)} 0 ${calcRem(10)} 0`,
};

const deviceSizes = {
  mobileS: "320px",
  mobileM: "375px",
  mobileL: "450px",
  tablet: `${mediaQuery.tabletMin}px`,
  tabletL: "1024px",
};

const device = {
  mobileS: `only screen and (max-width: ${deviceSizes.mobileS})`,
  mobileM: `only screen and (max-width: ${deviceSizes.mobileM})`,
  mobileL: `only screen and (max-width: ${deviceSizes.mobileL})`,
  tablet: `only screen and (max-width: ${deviceSizes.tablet})`,
  tabletL: `only screen and (max-width: ${deviceSizes.tabletL})`,
};

export const light = {
  value: "light",
  fontSizes,
  COLORS: {
    ...COLORS,
    ...LIGHT_THEME_COLORS,
  },
  deviceSizes,
  device,
  paddings,
  interval,
  verticalInterval,
};

export const dark = {
  value: "dark",
  fontSizes,
  COLORS: {
    ...COLORS,
    ...DARK_THEME_COLORS,
  },
  deviceSizes,
  device,
  paddings,
  interval,
  verticalInterval,
};

export const isLight = (theme: DefaultTheme): boolean =>
  theme.value === "light";

export const setThemeClass = (theme: "light" | "dark") => {
  if (theme === "light") {
    document.body.classList.add("light");
    document.body.classList.remove("dark");
  } else {
    document.body.classList.add("dark");
    document.body.classList.remove("light");
  }
};

export const getThemeConfig = (themeMode: ThemeText): ThemeConfig => {
  const theme = themeMode === "light" ? light : dark;
  const isDarkMode = themeMode === "dark";
  return {
    components: {
      Popover: {
        minWidth: 0,
        width: 0,
        padding: 0,
      },
      Tree: {
        directoryNodeSelectedBg: COLORS.SELECTED,
      },
      Select: isDarkMode
        ? {
            optionSelectedBg: COLORS.BUTTONS2,
            optionSelectedColor: COLORS.WHITE,
            optionActiveBg: `${COLORS.BUTTONS2}33`,
            colorPrimary: COLORS.BUTTONS2,
          }
        : undefined,
      Table: {
        cellPaddingBlock: 0,
        headerColor: `${theme.COLORS.WHITE}95`,
        headerSplitColor: "transparent",
        bodySortBg: "transparent",
        borderColor:
          themeMode === "light" ? "rgb(237, 237, 237)" : COLORS.BORDER,
        filterDropdownBg: COLORS.WHITE,
        colorFillTertiary: COLORS.BLACK,
        rowSelectedBg: `${theme.COLORS.SELECTED}10`,
        rowSelectedHoverBg: `${theme.COLORS.SELECTED}10`,
      },
      Modal: {
        contentBg: "transparent",
      },
    },
    token: {
      colorBgContainer: "transparent",
      borderRadiusOuter: 0,
      borderRadiusXS: 0,
      borderRadiusLG: 0,
      paddingSM: 0,
      // colorText: themeObj.COLORS.WHITE,
      colorLink: theme.COLORS.SELECTED,
      colorLinkActive: theme.COLORS.SELECTED,
      colorPrimary: theme.COLORS.SELECTED,
      fontFamily: baseFontFamily,
    },
  };
};

export const getSummaryCardStyle = (theme: DefaultTheme) => {
  return `
    background-color: ${theme.COLORS.CARD_BG};
    border: 1px solid ${theme.COLORS.CARD_BORDER};
    border-radius: 6px;
  `;
};
